import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ArrowPathIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { flexRender, getCoreRowModel, getExpandedRowModel, getPaginationRowModel, getSortedRowModel, RowModel, SortingState, Table, useReactTable } from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useSearchParams } from "react-router-dom";

import useApiHelper from "../../../hooks/useApiHelper";
import { PageMode } from "../../../models/PagedTable";
import { DefaultResponseWithData } from "../../../models/system";
import { ButtonInput } from "../Inputs/ButtonInput";
import LoadingWheel from "../LoadingWheel";
import apiConfig from "../../../utils/apiConfig";
import classNames from "../../../utils/classNames";
;


export interface IPagedResponse<T> extends DefaultResponseWithData<IPagedData<T>> { }

export interface IPagedData<T> {
  rows: T[];
  total: number;
  totalPages: number;
  page: number;
  pageSize: number;
}

export interface IPagedTableActionButton<T> {
  label: string;
  onClick: (rows: RowModel<T>, table: Table<T>) => void;
  disabledOnNoRows?: boolean;
  isLoading?: boolean;
}

export interface IPagedModeToggle {
  label: string;
  pageMode?: PageMode;
  urlQuery?: string;
}

export type OtherInfo = {
  label: string;
  value: string;
}

export interface IPagedTableProps<T> {
  url: string;
  columns: any;
  pageMode?: PageMode;
  queryLabel?: string;
  externalQuery?: string;
  modeToggles?: IPagedModeToggle[];
  customUrlQuery?: string;
  actionButtons?: IPagedTableActionButton<T>[];
  expandedRow?: (row: T) => JSX.Element;
  extraRow?: (row: T) => JSX.Element;
  onRowClick?: (row: T) => void;
  onRowDoubleClick?: (row: T) => void;
  showTotal?: boolean;
  additionalBody?: Record<string, any>;
  initialSort?: SortingState;
  setRowData?: (data: any) => void;
  rowColor?: (row: T) => string;
  otherInfo?: OtherInfo[];
  onTotalRowsChange?: (totalRows: number) => void;
  // When true, render the results/action/pagination toolbar below the table
  // rather than the default sticky position above it.
  paginationAtBottom?: boolean;
  /**
   * Renders the pagination toolbar at the very top of the paged table block, with
   * elevated (floating) styling. Often paired with `paginationAtBottom={false}`.
   */
  showFloatingPaginationTop?: boolean;
  /**
   * When set, paged list mode checkboxes are driven by the parent. Pair with
   * `onModeToggle` and set `renderModeTogglesInTable: false` to show toggles
   * elsewhere (e.g. a search form).
   */
  modeTogglesControlled?: boolean[];
  onModeToggle?: (index: number) => void;
  /** When false, the mode toggle checkboxes are not rendered in this component. */
  renderModeTogglesInTable?: boolean;
  /** Softer slate header/borders; matches newer dashboard pages. */
  slateTableChrome?: boolean;
  /**
   * When true, expanded rows (main + detail) share a distinct background and a
   * left accent on the detail block. Unlisted eBay list only — leave false for
   * all other paged tables.
   */
  highlightExpandedRowGroup?: boolean;
  /**
   * Renders a full-width row above the main data row (e.g. listing title).
   * Receives the row’s data object and whether the row is currently expanded.
   */
  aboveRowContent?: (row: T, context: { isExpanded: boolean }) => JSX.Element;
  /**
   * When true, applies subtle alternating row backgrounds (zebra striping) to
   * the row group (above-row, main-row, extra-row, expanded-row) for easier
   * scanning. Overridden by expanded-row highlight + custom `rowColor`.
   */
  stripedRows?: boolean;
}

export type DateRangeType = {
  fieldName: string;
  start: Date | null;
  end: Date | null;
}

const EMPTY_ROWS: any[] = [];

export default function PagedTable<T>(tableConfig: IPagedTableProps<T>) {
  const { post } = useApiHelper(apiConfig("PagedTable"));
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearchParam = searchParams.get("search") || "";
  const [query, setQuery] = useState<string>(urlSearchParam);
  const hasExternalQuery = tableConfig.externalQuery !== undefined;
  const modeToggleCount = tableConfig.modeToggles?.length ?? 0;
  const [internalToggles, setInternalToggles] = useState<boolean[]>(
    () => new Array(modeToggleCount).fill(false)
  );
  const toggles = tableConfig.modeTogglesControlled ?? internalToggles;
  const [sorting, setSorting] = useState<SortingState>(tableConfig.initialSort ?? []);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { setRowData, onTotalRowsChange } = tableConfig;

  const debouncedQuery = useDebounce(query, 1000);
  const debouncedAdditionalBody = useDebounce(tableConfig.additionalBody, 300);
  const pageParams = [debouncedQuery, pageIndex, toggles, sorting, pageSize];

  const getPageMode = useCallback(() => {
    const activeModes = tableConfig.modeToggles
      ?.filter((toggle, i) => toggle.pageMode && toggles[i])
      .map(toggle => toggle.pageMode);

    // If no modes active, return default; otherwise a joined string of all modes
    return !activeModes || activeModes.length === 0
      ? tableConfig.pageMode
      : activeModes.join(',');
  }, [tableConfig, toggles]);

  const currentPageMode = useMemo(() => getPageMode(), [getPageMode]);

  // Cleanup function to cancel pending requests
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const applyQuery = useCallback((newQuery: string, resetPage: boolean = true) => {
    setQuery(newQuery);
    if (resetPage) setPageIndex(1);

    // Keep URL in sync for deep linking/back-forward behaviour
    const newSearchParams = new URLSearchParams(searchParams);
    if (newQuery.trim()) {
      newSearchParams.set("search", newQuery);
    } else {
      newSearchParams.delete("search");
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Sync internal query from either external query prop or URL
  useEffect(() => {
    if (hasExternalQuery) {
      const external = tableConfig.externalQuery ?? "";
      if (external !== query) {
        applyQuery(external);
      }
      return;
    }

    const urlSearch = searchParams.get("search") || "";
    if (urlSearch !== query) {
      setQuery(urlSearch);
    }
  }, [hasExternalQuery, tableConfig.externalQuery, searchParams, query, applyQuery]);


  const getUrl = useCallback(() => {
    const { url, customUrlQuery, modeToggles } = tableConfig;

    const queryString =
      modeToggles
        ?.filter(toggle => toggle.urlQuery)
        .map(toggle => toggle.urlQuery)
        .join("&") ?? "";

    const customQuery = customUrlQuery ? `&${customUrlQuery}` : "";
    const modeQuery = currentPageMode;

    let finalUrl = `${url}/${pageIndex}?pageMode=${modeQuery}`;

    if (pageSize) finalUrl += `&pageSize=${pageSize}`;

    // Add the queries if they exist
    if (debouncedQuery.trim()) finalUrl += `&query=${debouncedQuery}`;

    if (queryString) finalUrl += `&${queryString}`;

    // Append the custom query if provided
    finalUrl += customQuery;

    return finalUrl;
  }, [tableConfig, debouncedQuery, pageIndex, getPageMode, pageSize]);

  const getSort = useCallback(() => {
    let sort: any = {};

    if (sorting.length != 0) {
      let matchingCol = tableConfig.columns.filter((x: any) => x.id == sorting[0]?.id)[0];
      let key = matchingCol?.accessorKey ? matchingCol.accessorKey : sorting[0].id;

      sort = { ...sorting[0], id: key };
    }

    return sort;
  }, [sorting, tableConfig.columns]);


  const requestBody = useMemo(() => {
    return {
      ...getSort(),
      searchBody: {
        ...debouncedAdditionalBody
      }
    };
  }, [sorting, tableConfig.columns, debouncedAdditionalBody]);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ["pagedTable", tableConfig.url, currentPageMode, debouncedAdditionalBody, ...pageParams],
    ({ signal }) => {
      // Cancel any previous request
      cleanup();

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      return post<any>(getUrl(), requestBody, abortControllerRef.current.signal) as Promise<IPagedResponse<T>>;
    },
    {
      keepPreviousData: true,
      staleTime: 1000 * 60 * 2, // 2 minutes
      refetchInterval: 1000 * 60 * 5, // 5 minutes
      retry: 1, // Limit retries to prevent infinite loops
      retryDelay: 1000, // Wait 1 second before retrying
      onError: (error) => {
        console.error('PagedTable query error:', error);
      },
    },
  );

  useEffect(() => {
    if (data?.data.rows && setRowData) setRowData(data.data.rows);
  }, [data?.data.rows]);

  useEffect(() => {
    if (typeof data?.data.total === "number" && onTotalRowsChange) {
      onTotalRowsChange(data.data.total);
    }
  }, [data?.data.total, onTotalRowsChange]);

  const table = useReactTable({
    data: data?.data.rows ?? EMPTY_ROWS,
    columns: tableConfig.columns,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    // Whenever data rows change, clear selection for any deleted rows
    if (table.getState().rowSelection && data?.data?.rows) {
      const validRowIds = new Set(data.data.rows.map((_, i) => i.toString()));
      const currentSelection = table.getState().rowSelection;

      // If any selected IDs are no longer valid, reset
      const hasInvalid = Object.keys(currentSelection).some(
        (key) => !validRowIds.has(key)
      );
      if (hasInvalid) {
        table.resetRowSelection();
      }
    }
  }, [data?.data?.rows, table]);

  useEffect(() => {
    if (data && !isLoading) {
      table.setPageSize(data.data.pageSize);
    }
  }, [data?.data?.pageSize, isLoading]);

  // useEffect(() => {
  //   console.log("Sorting changed:", getSort());
  // }, [sorting]);

  const changeQuery = (newQuery: string) => {
    applyQuery(newQuery);
  };

  const changePage = (increment: number) => {
    if (isLoading) return;
    setPageIndex(pageIndex + increment);
  }

  const handleToggle = (index: number) => {
    if (isLoading) return;

    if (tableConfig.onModeToggle) {
      tableConfig.onModeToggle(index);
      return;
    }

    setInternalToggles((prev) => {
      const updatedToggles = [...prev];
      updatedToggles[index] = !updatedToggles[index];
      return updatedToggles;
    });
  };

  // useEffect(() => {
  //   let rows = table.getRowModel();
  // }, [table.getSelectedRowModel()]);

  const lastUrlRef = useRef<string | null>(null);
  const lastBodyRef = useRef<string | null>(null);

  const memoizedUrl = useMemo(() => getUrl(), [getUrl]);
  const memoizedBody = useMemo(() => JSON.stringify(requestBody), [requestBody]);

  useEffect(() => {
    if (data && !isLoading && !isFetching) {
      lastUrlRef.current = memoizedUrl;
      lastBodyRef.current = memoizedBody;
    }
  }, [data, isLoading, isFetching, memoizedUrl, memoizedBody]);

  // Determine if we should show loading
  const shouldShowLoading = isLoading ||
    (isFetching && lastUrlRef.current !== memoizedUrl) ||
    (isFetching && lastBodyRef.current !== memoizedBody);


  // Determine if we should show error state
  const shouldShowError = error && !data?.data?.rows?.length && !isLoading;

  // Check if error is an abort error (which is expected during cleanup)
  const isAbortError = error && (
    (error as any)?.name === 'AbortError' ||
    (error as any)?.message?.includes('aborted')
  );

  // Show error state only if we have a real error and no previous data
  if (shouldShowError && !isAbortError) {
    return (
      <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-800">Error loading data. Please try again.</p>
        <ButtonInput
          label="Retry"
          onClick={() => refetch()}
          classes="mt-2"
        />
      </div>
    );
  }

  // Results / selection summary, action buttons and pagination controls.
  const slateChrome = tableConfig.slateTableChrome;
  const showModeTogglesInTable = tableConfig.renderModeTogglesInTable !== false;

  const renderPaginationToolbar = (barId: "floatTop" | "belowSearch" | "bottom") => (
    <div
      className={classNames(
        "rounded-lg border border-slate-200 bg-white/95 px-3 py-2 shadow-md backdrop-blur supports-[backdrop-filter]:bg-white/80",
        // Match dashboard Topbar (h-16) so the bar stays visible below the nav when scrolling
        barId === "floatTop" &&
          "sticky top-16 z-20 mb-3 rounded-xl py-2.5 shadow-lg ring-1 ring-slate-200/80",
        barId === "belowSearch" && !tableConfig.paginationAtBottom && "sticky top-16 z-20 mt-3",
        barId === "bottom" && tableConfig.paginationAtBottom && "mt-3"
      )}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-2 text-sm text-slate-700">
          {tableConfig.showTotal && (
            <span>
              <span className="font-semibold">{data?.data.total ?? table.getPreFilteredRowModel()?.rows?.length ?? 0}</span>{" "}
              <span className="text-slate-500">Results</span>
            </span>
          )}
          {table.getSelectedRowModel().rows.length > 0 && (
            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary-700">
              {table.getSelectedRowModel().rows.length} selected
            </span>
          )}
        </div>
        {tableConfig.actionButtons && tableConfig.actionButtons.length > 0 && (
          <div className="flex flex-1 flex-wrap items-center justify-center gap-2">
            {tableConfig.actionButtons.map((button, i) =>
              button.isLoading ? (
                <ButtonInput key={i} disabled={true} isSubmit={false} onClick={() => button.onClick(table.getSelectedRowModel(), table)} classes={"!bg-primary-600 px-3 py-2 text-white"}>
                  <ArrowPathIcon className="animate-spin bg-primary-600 h-5 w-5" />
                </ButtonInput>
              ) : (
                <ButtonInput key={i} label={button.label} disabled={button.disabledOnNoRows ? table.getSelectedRowModel().rows.length === 0 : false} isSubmit={false} onClick={() => button.onClick(table.getSelectedRowModel(), table)} classes={""} />
              )
            )}
          </div>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-3 text-sm">
          <div className="flex items-center gap-1">
            <ButtonInput label={"<"} isSubmit={false} onClick={() => changePage(-1)}
              disabled={!(pageIndex > 1)} classes="!px-2 !py-1 text-base"
            />
            <ButtonInput label={">"} isSubmit={false} onClick={() => changePage(1)}
              disabled={!(pageIndex < data?.data?.totalPages!)} classes="!px-2 !py-1 text-base"
            />
          </div>
          <span className="flex items-center gap-1 text-slate-600">
            <span>Page</span>
            <strong className="text-slate-800">
              {data?.data.page ?? 1} of {data?.data.totalPages ?? 1}
            </strong>
          </span>
          <span className="flex items-center gap-1 text-slate-600">
            <span>Go to:</span>
            <input
              type="number"
              defaultValue={pageIndex}
              onChange={e => {
                let page = e.target.value ? Number(e.target.value) : 0;
                page = page < 1 || page > data?.data.totalPages! ? 1 : page;
                setPageIndex(page);
              }}
              className="border p-1 rounded w-16 text-sm"
            />
          </span>
          <select
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value))
            }}
            className="border p-1 rounded w-28 text-sm"
          >
            {[10, 30, 50, 100, 200, 1000].map(size => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  const hasSearchOrToggleOrOtherInfo =
    Boolean(tableConfig.queryLabel) ||
    (showModeTogglesInTable && (tableConfig.modeToggles?.length ?? 0) > 0) ||
    Boolean(tableConfig.otherInfo && tableConfig.otherInfo.length > 0);

  return (
    <div>
      {tableConfig.showFloatingPaginationTop && renderPaginationToolbar("floatTop")}

      {hasSearchOrToggleOrOtherInfo && (
        <div className="mt-4 flex justify-between">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {tableConfig.queryLabel ? (
              <div className="relative flex min-w-0 flex-grow items-stretch focus-within:z-10">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon
                    className={classNames("h-5 w-5", slateChrome ? "text-slate-400" : "text-gray-400")}
                    aria-hidden="true"
                  />
                </div>
                <input
                  type="text"
                  value={query}
                  disabled={isLoading}
                  onChange={(e) => changeQuery(e.target.value)}
                  className={classNames(
                    "pl-10 sm:text-sm lg:min-w-[250px] sm:min-w-[unset] max-w-full lg:max-w-[calc(100% - 3rem)] border rounded-md focus:outline-none focus:ring-1",
                    slateChrome
                      ? "border-slate-300 text-slate-800 placeholder:text-slate-400 focus:border-primary-500 focus:ring-primary-500"
                      : "border-1 border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                  )}
                  placeholder={tableConfig.queryLabel!}
                  style={{ width: `calc(${tableConfig.queryLabel!.length}ch - 2rem)` }}
                />
              </div>
            ) : null}

            {showModeTogglesInTable &&
              tableConfig.modeToggles?.map((mt, i) => (
                <div className="flex items-center" key={i}>
                  <input
                    onChange={() => handleToggle(i)}
                    checked={Boolean(toggles[i])}
                    type="checkbox"
                    className={classNames(
                      "mr-2 h-5 w-5",
                      slateChrome && "rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    )}
                  />
                  <label className={classNames("text-sm", slateChrome ? "text-slate-600" : "")}>
                    {mt.label}
                  </label>
                </div>
              ))}
          </div>

          <div className="flex flex-col items-end gap-2">
            {tableConfig.otherInfo && tableConfig.otherInfo.length > 0 && tableConfig.otherInfo.map((info, i) => (
              <div className="text-sm" key={i}>
                {info?.label}: {(data?.data as any)?.[info?.value]}
              </div>
            ))}
          </div>
        </div>
      )}

      {!tableConfig.paginationAtBottom && !tableConfig.showFloatingPaginationTop && renderPaginationToolbar("belowSearch")}
      <div className={classNames("mt-4 overflow-auto", slateChrome ? "rounded-b-lg bg-white" : "bg-white")}>
        <table
          className={classNames(
            "min-w-full overflow-scroll divide-y",
            slateChrome
              ? "divide-slate-200 border border-slate-200"
              : "border divide-gray-300"
          )}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup, i) => (
              <tr
                key={`headerGroup-${headerGroup.id}-${i}`}
                className={classNames(
                  slateChrome
                    ? "bg-slate-50/90 text-slate-800"
                    : "bg-primary-50 text-gray-800"
                )}
              >
                {headerGroup.headers.map((header, j) => (
                  <th
                    key={`header-${header.id}-${j}`}
                    scope="col"
                    className={classNames(
                      "py-3.5 px-3 text-sm font-semibold lg:table-cell text-left",
                      (header.column.columnDef.meta as any)?.thClassName ?? ""
                    )}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={
                          header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : ""
                        }
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: " ▲",
                          desc: " ▼",
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {/* Loading state keeps its own tbody */}
          {shouldShowLoading && (
            <tbody>
              <tr>
                <td colSpan={12}>
                  <div className="flex justify-center py-4">
                    <LoadingWheel />
                  </div>
                </td>
              </tr>
            </tbody>
          )}

          {/* Actual rows */}
          {!shouldShowLoading &&
            table.getRowModel().rows.map((row, index) => {
              const rowCells = row.getVisibleCells();
              // Ensure unique key by combining row.id (which is unique from TanStack Table) with index as fallback
              const rowKey = `row-${row.id}-${index}`;
              const isExpanded = Boolean(
                tableConfig.expandedRow && row.getIsExpanded()
              );
              const showExpandedGroupHighlight = Boolean(
                tableConfig.highlightExpandedRowGroup && isExpanded
              );
              const expandedRowBg = slateChrome
                ? "bg-slate-100/95 hover:bg-slate-100"
                : "bg-primary-50/80 hover:bg-primary-50/90";
              const stripeBg =
                tableConfig.stripedRows && !showExpandedGroupHighlight
                  ? index % 2 === 1
                    ? slateChrome
                      ? "bg-slate-50/70"
                      : "bg-gray-50/70"
                    : "bg-white"
                  : "";

              return (
                <tbody key={rowKey} className="group">
                  {tableConfig.aboveRowContent && (
                    <tr
                      key={`above-${rowKey}`}
                      className={classNames(stripeBg || "bg-white")}
                    >
                      <td
                        colSpan={rowCells.length}
                        className={classNames(
                          "px-4 py-4 text-left text-sm leading-snug sm:px-5 sm:py-5",
                          slateChrome ? "text-slate-800" : "text-gray-900"
                        )}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {tableConfig.aboveRowContent(row.original, {
                          isExpanded: row.getIsExpanded(),
                        })}
                      </td>
                    </tr>
                  )}
                  {/* Main row */}
                  <tr
                    onClick={() => tableConfig.onRowClick?.(row.original)}
                    onDoubleClick={() => tableConfig.onRowDoubleClick?.(row.original)}
                    className={classNames(
                      showExpandedGroupHighlight
                        ? expandedRowBg
                        : slateChrome
                          ? classNames(stripeBg, "group-hover:bg-slate-100/70")
                          : classNames(stripeBg, "group-hover:bg-gray-100"),
                      tableConfig.onRowClick || tableConfig.onRowDoubleClick ? "hover:cursor-pointer" : "",
                      tableConfig.onRowDoubleClick ? "select-none" : "",
                      tableConfig.rowColor?.(row.original) ?? ""
                    )}
                  >
                    {rowCells.map((cell, i) => (
                      <td
                        key={`cell-${cell.id}-${i}`}
                        className={classNames(
                          "px-3 py-3 text-sm",
                          slateChrome ? "border-slate-200 border" : "border",
                          (cell.column.columnDef.meta as any)?.tdClassName ?? ""
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>

                  {/* Extra row */}
                  {tableConfig.extraRow && (
                    <tr
                      key={`extra-${rowKey}`}
                      onClick={() => tableConfig.onRowClick?.(row.original)}
                      onDoubleClick={() => tableConfig.onRowDoubleClick?.(row.original)}
                      className={classNames(
                        showExpandedGroupHighlight
                          ? expandedRowBg
                          : slateChrome
                            ? classNames(stripeBg, "group-hover:bg-slate-100/70")
                            : classNames(stripeBg, "group-hover:bg-gray-100"),
                        tableConfig.onRowClick || tableConfig.onRowDoubleClick ? "hover:cursor-pointer" : "",
                        tableConfig.rowColor?.(row.original) ?? ""
                      )}
                    >
                      <td className={classNames(slateChrome ? "border-slate-200 border-r" : "border-r")} />
                      <td colSpan={rowCells.length}>
                        {tableConfig.extraRow(row.original)}
                      </td>
                    </tr>
                  )}

                  {/* Expanded row */}
                  {row.getIsExpanded() && (
                    <tr
                      key={`expanded-${rowKey}`}
                      className={classNames(
                        showExpandedGroupHighlight
                          ? expandedRowBg
                          : slateChrome
                            ? classNames(stripeBg, "group-hover:bg-slate-100/70")
                            : classNames(stripeBg, "group-hover:bg-gray-100"),
                        showExpandedGroupHighlight &&
                          (slateChrome
                            ? "border-t border-slate-200/80"
                            : "border-t border-gray-200")
                      )}
                    >
                      {showExpandedGroupHighlight ? (
                        <td colSpan={rowCells.length} className="p-0 sm:p-0">
                          <div
                            className={classNames(
                              slateChrome
                                ? "border-l-4 border-primary-500/90 pl-2"
                                : "border-l-4 border-primary-500 pl-2"
                            )}
                          >
                            {tableConfig.expandedRow?.(row.original)}
                          </div>
                        </td>
                      ) : (
                        <td colSpan={rowCells.length}>
                          {tableConfig.expandedRow?.(row.original)}
                        </td>
                      )}
                    </tr>
                  )}
                </tbody>
              );
            })}
        </table>


      </div>
      {tableConfig.paginationAtBottom && renderPaginationToolbar("bottom")}
    </div>
  );
}